/* resizePatientPhoto — client-side photo prep for the doctor's "upload
 * patient photo" flow.
 *
 * Reads a File, cover-crops it square at `size`px, and returns a JPEG
 * data URL. We do this in the browser so the backend never sees images
 * larger than 250KB (server caps at ~350K base64 chars).
 */

export async function resizePatientPhoto(file, size = 256) {
    if (!file) throw new Error("No file");
    const bitmap = await loadImage(file);

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");

    // Cover-crop maths: pick the smallest source-edge, centre-crop.
    const srcW = bitmap.width;
    const srcH = bitmap.height;
    const srcSide = Math.min(srcW, srcH);
    const sx = (srcW - srcSide) / 2;
    const sy = (srcH - srcSide) / 2;
    ctx.drawImage(bitmap, sx, sy, srcSide, srcSide, 0, 0, size, size);

    // Re-encode as JPEG quality 0.85 — good enough for a 256² portrait
    // and reliably under the server's 250KB cap.
    return canvas.toDataURL("image/jpeg", 0.85);
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error || new Error("Read failed"));
        reader.onload = () => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Image decode failed"));
            img.src = String(reader.result || "");
        };
        reader.readAsDataURL(file);
    });
}

export default resizePatientPhoto;
